import { RefObject, useEffect, useEffectEvent, useRef, useState } from "react"
import { useTimeoutFn } from "react-use"
import { useAtom, useSetAtom } from "jotai"

import { useSetupConnectListeners } from "@/agent-state/connect-listeners"
import { useErrorToast } from "@/components/CallPanel/call-error-card"
import { useBrowserErrorModal } from "@/components/modals/BrowserErrorModal"
import { useMicrophoneErrorModal } from "@/components/modals/MicrophoneErrorModal"
import { config } from "@/config/index"
import { ccpModeAtom, isMutedAtom } from "@/helpers/atoms"
import { getIframeContainer } from "@/helpers/ccp"
import { connectLogoutUrl, useAuthHook } from "@/hooks/auth"
import { ConnectMode } from "@/types/modes"

import {
  clearConnectTimestamp,
  setTimestampInLocalStorage,
} from "./connect_session"
import {
  initAgentEventHandlers,
  initContactEventHandlers,
  initErrorHandlers,
} from "./init_ccp"
import { SetAgent } from "./types"

interface ConnectCCPType {
  connectMode: ConnectMode
  hiddenFormRef: RefObject<HTMLFormElement | null>
}

const ccpUrl = `https://${config.connectInstanceAlias}.my.connect.aws/connect/ccp-v2`

const initCCPOptions = {
  ccpUrl,
  region: config.awsRegion,
  loginPopup: false,
  loginOptions: {
    autoClose: true,
  },
  loginPopupAutoClose: true,
  softphone: { allowFramedSoftphone: true },
  task: { disableRingtone: true },
}

const useConnectCCPHook = (): ConnectCCPType => {
  const { getConnectCredentials } = useAuthHook()

  const { onAuthenticated } = useSetupConnectListeners()
  const { showBrowserErrorModal } = useBrowserErrorModal()
  const { showMicrophoneErrorModal } = useMicrophoneErrorModal()

  // React refs to the div element where the CCP iframe will be mounted
  const hiddenFormRef = useRef<HTMLFormElement | null>(null)
  // Put the agent and the contact in a ref so that Amazon Connect event handlers
  // (running outside of the react app) can access the updated values
  const agentRef = useRef<connect.Agent | null>(null)
  // initCCP and handler registration must only happen once.
  // Re-authentication refreshes the cookie; the existing iframe and handlers stay valid.
  const ccpInitializedRef = useRef(false)

  const [connectMode, setConnectMode] = useState<ConnectMode>({
    current: "AUTHENTICATING",
    state: { credentials: null },
  })

  const [ccpMode, setCCPMode] = useAtom(ccpModeAtom)
  const setIsMuted = useSetAtom(isMutedAtom)
  const { pushNetworkingError, resetNetworkingError } = useErrorToast()

  // Hook to display a message to the agent if the initialization is slow
  const [isReady, cancel] = useTimeoutFn(() => {
    if (ccpMode.current === "LOADING") {
      setCCPMode({
        current: "LOADING",
        isSlow: true,
      })
    }
  }, config.slowInitializationThreshold)

  const shouldFetchConnectCredentials = connectMode.current === "AUTHENTICATING"
  const shouldPostConnectCredentials =
    connectMode.current === "WITH_CREDENTIALS"
  const shouldInitCCPIframe = connectMode.current === "AUTHENTICATED"

  const setAgent: SetAgent = (agent) => {
    agentRef.current = agent
  }

  const resetIsMuted = () => setIsMuted(false)

  // Use the Amazon Connect Federated token to authenticate the CCP iframe
  // and put the federated token in state
  const getConnectFederatedToken = useEffectEvent(async () => {
    try {
      // Exchange the AWS Cognito token for an AWS Connect federated token
      const loginCredentials = await getConnectCredentials()

      setConnectMode({
        current: "WITH_CREDENTIALS",
        state: { credentials: loginCredentials },
      })

      // Store date when Amazon connect credentials were retrieved to know when it's time
      // to refresh the federated token
      setTimestampInLocalStorage()
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Unknown connect authentication error"

      setConnectMode({
        current: "NOT_AUTHENTICATED",
        state: { credentials: null, errorMessage },
      })
    }
  })

  const getConnectCookie = useEffectEvent(() => {
    if (!shouldPostConnectCredentials) {
      return
    }

    // Post credentials to the connect login route; See https://stackoverflow.com/a/4702110
    hiddenFormRef.current?.submit()

    setConnectMode({
      current: "AUTHENTICATED",
      state: { credentials: connectMode.state.credentials },
    })
  })

  // The AWS Connect access token is valid for 12 hours. We'll refresh the token after 10 hours
  // to prevent the edge case when the agent gets disconnected while in a super long call (e.g. 2 hour call).
  const refreshConnectSession = async () => {
    setCCPMode({ current: "LOADING" })

    try {
      // Sign out from Amazon Connect
      // TODO: In a future iteration, use the refresh token route instead of the logout route
      await fetch(connectLogoutUrl, { credentials: "include", mode: "no-cors" })
      clearConnectTimestamp()

      // Restart the AWS Connect authentication process; will request a new cookie with the AWS Connect access token
      setConnectMode({
        current: "AUTHENTICATING",
        state: { credentials: null },
      })
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Unknown connect authentication error"

      setConnectMode({
        current: "NOT_AUTHENTICATED",
        state: { credentials: null, errorMessage },
      })
    }
  }

  const setInitialized = () => {
    setCCPMode({ current: "INITIALIZED" })

    // Cancel the slow-loading state after the CCP has been initialized
    if (isReady() === false) {
      cancel()
    }
  }

  const initializeCCPIframe = () => {
    // Set the log level; Will not mute iframe internal logs. Only the custom added logs
    // https://github.com/amazon-connect/amazon-connect-streams/issues/269
    const connectLogger = connect.getLog()
    connectLogger.setEchoLevel(connect.LogLevel.WARN)
    connectLogger.setLogLevel(connect.LogLevel.WARN)

    const iframeContainer = getIframeContainer()
    connect.core.initCCP(iframeContainer, initCCPOptions)
    connect.core.onInitialized(setInitialized)

    // Initialize all iframe error handlers
    initErrorHandlers({ setCCPMode })

    // Initialize event handlers for agent events
    onAuthenticated()
    initAgentEventHandlers({
      agentRef,
      setAgent,
      refreshConnectSession,
      pushNetworkingError,
      resetNetworkingError,
      showBrowserErrorModal,
      showMicrophoneErrorModal,
    })

    // Initialize event handlers for contact events
    initContactEventHandlers({
      agentRef,
      resetIsMuted,
      refreshConnectSession,
    })
  }

  // Initialize the CCP iframe a single time. On AWS Connect credentials refresh
  // do not re-initialize the CCP iframe and handlers.
  const initConnectIframe = useEffectEvent(() => {
    if (ccpInitializedRef.current) {
      setCCPMode({ current: "INITIALIZED" })

      return
    }

    initializeCCPIframe()
    ccpInitializedRef.current = true
  })

  // 1. Get the Amazon Connect Federation token
  useEffect(() => {
    if (!shouldFetchConnectCredentials) {
      return
    }

    getConnectFederatedToken()
  }, [shouldFetchConnectCredentials])

  // 2. Post the Amazon Connect credentials to the Connect login route and retrieve a cookie in the iframe
  // This second hook is required because the form.post() method
  // sends the connect credentials that are stored in state
  useEffect(() => {
    getConnectCookie()
  }, [shouldPostConnectCredentials])

  // 3. Once we have the cookie with credentials we can initialize the CCP iframe and the Customer Profiles Client (iframe)
  useEffect(() => {
    if (shouldInitCCPIframe) initConnectIframe()
  }, [shouldInitCCPIframe])

  return {
    hiddenFormRef,
    connectMode,
  }
}

export { useConnectCCPHook }
